import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from "react";

import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

export type SwitchSize = "default" | "sm";

const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitives.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
    isLoading?: boolean;
    size?: SwitchSize;
  }
>(({ className, isLoading = false, size = "default", ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cnTwMerge(
      "peer relative inline-flex items-center rounded-full transition-colors",
      "p-[1px] gap-2",
      "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-brand-primary focus-visible:ring-offset-background",
      "data-[state=checked]:bg-text/interactive data-[state=unchecked]:bg-background/tertiary",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "border border-border/primary",
      size === "default" && "h-7 w-12",
      size === "sm" && "h-[18px] w-[30px]",
      isLoading && "disabled:pointer-events-none skeleton-box",
      className,
    )}
    ref={ref}
    {...props}
  >
    <SwitchPrimitives.Thumb
      className={cnTwMerge(
        "pointer-events-none block rounded-full bg-background/primary transition-transform",
        "data-[state=unchecked]:translate-x-0",
        "border border-border/primary shadow-md",
        size === "default" && "h-6 w-6 data-[state=checked]:translate-x-5",
        size === "sm" && "h-[14px] w-[14px] data-[state=checked]:translate-x-3",
        isLoading && "disabled:pointer-events-none skeleton-box",
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
